// SPDX-FileCopyrightText: 2026 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { useAuthContext } from '@tls/auth';
import { Colors } from '@tls/treact-ui';
import { UicSidebar } from '@trumpf/ux.ui-community-react';
import { useNoiseConfig } from 'hooks/useNoiseConfig';
import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { AppRoutes } from 'utilities/router/AppRoutes';

type Props = {
  open: boolean;
  onClose: () => void;
  /** Elements whose clicks must not close the sidebar (e.g. the bell). */
  ignoreCloseRef?: React.RefObject<HTMLElement | null>;
};

/** Hosts uxn-notification-center; stays mounted while closed since the bell shares its store. */
export default function NotificationSidebar({ open, onClose, ignoreCloseRef }: Readonly<Props>) {
  const { stage, locale } = useNoiseConfig();
  const { appBaseUrl } = useAuthContext();
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);

  const basename = appBaseUrl ?? ''; // also the BrowserRouter basename
  const settingsUrl = `${basename}${AppRoutes.SettingsNotifications}`;

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (ref.current?.contains(target) || ignoreCloseRef?.current?.contains(target)) return;
      onClose();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose, ignoreCloseRef]);

  // center renders plain <a href>, route in-app instead of reloading the page
  const onClick = (e: React.MouseEvent) => {
    const anchor = (e.target as HTMLElement).closest('a[href]');
    if (!anchor) return;
    const url = new URL(anchor.getAttribute('href') ?? '', window.location.href);
    if (url.origin !== window.location.origin || !url.pathname.startsWith(settingsUrl)) return;
    e.preventDefault();
    onClose();
    navigate(url.pathname.slice(basename.length) + url.search); // navigate re-adds the basename
  };

  return (
    <SidebarScope ref={ref} onClick={onClick} data-testid='notification-sidebar'>
      <UicSidebar side='right' alignHeader opened={open}>
        {stage && (
          <uxn-notification-center
            stage={stage}
            locale={locale}
            debug={stage !== 'PROD'}
            custom-settings-url={settingsUrl}
          />
        )}
      </UicSidebar>
    </SidebarScope>
  );
}

// uic-sidebar is position: fixed, the scope only carries its styles
const SidebarScope = styled.div`
  display: contents;

  uic-sidebar {
    width: min(24rem, 100vw);
    z-index: 23; /* below header (24) so its dropdowns stay on top */
    padding: 0 0.5rem 0.5rem;
    background-color: ${Colors.trwhite.hex};
    border-left: 1px solid ${Colors.trgrey2.hex};
    box-shadow: rgb(161 161 161) 0 0 6px 0;
  }

  /* status classes (and with them --closed) only exist after hydration */
  uic-sidebar:not(.hydrated) {
    display: none;
  }
`;
